// src/components/ShareCodeViewer.jsx
import React, { useState, useEffect, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stage, useGLTF, useFBX } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const GOLD = '#B8860B';
const NAVY = '#001F3F';

// --- UNIVERSAL MODEL LOADER ---
// Dynamically switches between GLTF and FBX based on file extension
function ModelLoader({ url, scale = 1 }) {
  const isFBX = url?.toLowerCase().endsWith('.fbx');
  
  // Hooks must be called at the top level, but we handle the null logic
  const gltf = !isFBX ? useGLTF(url) : null;
  const fbx = isFBX ? useFBX(url) : null;

  const scene = isFBX ? fbx : gltf.scene;

  return <primitive object={scene} scale={scale} />;
}

// --- 3D CRANIAL SOLID COMPONENT ---
function CranialSolid({ headUrl, hairUrl }) {
  return (
    <Stage environment="studio" intensity={0.5} adjustCamera={1.5}>
      <group>
        <Suspense fallback={null}>
          <ModelLoader 
            url={headUrl || '/models/default-cranial-solid.glb'} 
            scale={1.5} 
          />
        </Suspense>
        <Suspense fallback={null}>
          <ModelLoader 
            url={hairUrl || '/models/default-hair.glb'} 
            scale={1.52} 
          />
        </Suspense>
      </group>
    </Stage>
  );
}

// Pre-load the default cranial base for performance
useGLTF.preload('/models/default-cranial-solid.glb');

export default function ShareCodeViewer() {
  const { code: urlCode } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(urlCode || '');
  const [sharedData, setSharedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active3D, setActive3D] = useState(null);

  // Automatically attempt verification if code is passed via URL params
  useEffect(() => {
    if (urlCode) {
      handleVerify(null, urlCode);
    }
  }, [urlCode]);

  const handleVerify = async (e, directCode) => {
    if (e) e.preventDefault();
    const targetCode = directCode || code;
    
    if (targetCode.length !== 6) return toast.error("Enter a 6-digit code");

    setLoading(true);
    try {
      const codeRef = doc(db, 'share_codes', targetCode);
      const codeSnap = await getDoc(codeRef);

      if (!codeSnap.exists()) {
        toast.error("Invalid or Expired Code");
      } else {
        const data = codeSnap.data();
        
        // Expiry Check (Strict 2-hour security window)
        const now = new Date();
        if (data.expiresAt.toDate() < now) {
          toast.error("This code has expired.");
        } else {
          setSharedData(data);
          toast.success("Access Granted");
        }
      }
    } catch (err) {
      toast.error("Verification failed");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={container}>
      {!sharedData ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={loginBox}>
          <h1 style={title}>BARBER <span style={{ color: '#fff' }}>ACCESS</span></h1>
          <p style={subtitle}>Enter the 6-digit code provided by your client</p>
          <form onSubmit={handleVerify}>
            <input
              style={codeInput}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              maxLength="6"
            />
            <button style={verifyBtn} disabled={loading}>
              {loading ? 'VERIFYING...' : 'VIEW VAULT'}
            </button>
          </form>
        </motion.div>
      ) : (
        <div style={vaultContent}>
          <header style={vaultHeader}>
            <button onClick={() => setSharedData(null)} style={backBtn}>← EXIT</button>
            <div style={{ flex: 1, marginLeft: '20px' }}>
                <h2 style={clientName}>{sharedData.userName}'S VAULT</h2>
                <p style={{ margin: 0, fontSize: '0.7rem', opacity: 0.5 }}>TEMPORARY ACCESS GRANTED</p>
            </div>
            <button onClick={() => navigate('/login')} style={upgradeBtn}>
                FORMAL CONSULTATION
            </button>
          </header>

          <div style={grid}>
            {sharedData.lookbook.map((style, idx) => (
              <div key={idx} style={card}>
                <div style={styleImgContainer}>
                   <img src={style.imageUrl} alt={style.name} style={styleImg} />
                </div>
                <div style={info}>
                  <h3 style={styleTitle}>{style.name}</h3>
                  <button 
                    onClick={() => setActive3D(style)} 
                    style={viewBtn}
                  >
                    3D CRANIAL VIEW
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3D Modal - Focus on Cranial Solid & Health Alerts */}
      <AnimatePresence>
        {active3D && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={modalOverlay}>
            <div style={modalBody}>
              <div style={modalHeader}>
                <h3 style={{ color: GOLD, margin: 0, fontSize: '1rem' }}>3D CRANIAL PROJECTION</h3>
                <button onClick={() => setActive3D(null)} style={closeBtn}>✕</button>
              </div>

              <div style={canvasContainer}>
                <Canvas camera={{ position: [0, 0, 4] }}>
                  <Suspense fallback={null}>
                    <CranialSolid 
                       headUrl={sharedData.headModelUrl}
                       hairUrl={active3D.styleModelUrl} 
                    />
                    <OrbitControls enablePan={false} />
                  </Suspense>
                </Canvas>
              </div>

              <div style={healthSection}>
                <p style={healthLabel}>EARLY ACCESS HEALTH ALERT</p>
                <div style={alertBox}>
                   STRUCTURAL ANALYSIS: DENSITY CHECK REQUIRED FOR THIS STYLE
                </div>
                <button onClick={() => setActive3D(null)} style={returnBtn}>RETURN TO LOOKBOOK</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// STYLES
const container = { minHeight: '100vh', background: '#010810', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat, sans-serif' };
const loginBox = { textAlign: 'center', background: 'rgba(255,255,255,0.02)', padding: '50px', borderRadius: '30px', border: `1px solid ${GOLD}33`, maxWidth: '400px', width: '90%' };
const title = { color: GOLD, letterSpacing: '3px', margin: '0 0 10px', fontWeight: '900' };
const subtitle = { opacity: 0.5, fontSize: '0.8rem', marginBottom: '30px' };
const codeInput = { background: 'none', border: `2px solid ${GOLD}`, borderRadius: '15px', color: '#fff', fontSize: '2.5rem', textAlign: 'center', width: '100%', marginBottom: '20px', letterSpacing: '10px', outline: 'none' };
const verifyBtn = { background: GOLD, color: '#000', border: 'none', width: '100%', padding: '15px', borderRadius: '15px', fontWeight: '900', cursor: 'pointer' };

const vaultContent = { width: '100%', maxWidth: '1200px', padding: '40px 20px' };
const vaultHeader = { display: 'flex', alignItems: 'center', marginBottom: '40px', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '20px' };
const backBtn = { background: 'none', border: `1px solid ${GOLD}`, color: GOLD, padding: '8px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' };
const upgradeBtn = { background: '#fff', color: '#000', border: 'none', padding: '10px 22px', borderRadius: '12px', fontWeight: '900', cursor: 'pointer', fontSize: '0.75rem', letterSpacing: '1px' };
const clientName = { margin: 0, fontSize: '1.4rem', color: GOLD, fontWeight: '900', letterSpacing: '1px' };

const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '25px' };
const card = { background: '#050a14', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.05)' };
const styleImgContainer = { height: '300px', overflow: 'hidden' };
const styleImg = { width: '100%', height: '100%', objectFit: 'cover' };
const info = { padding: '20px', textAlign: 'center' };
const styleTitle = { margin: '0 0 15px', fontSize: '0.9rem', color: GOLD, textTransform: 'uppercase', fontWeight: '700' };
const viewBtn = { background: GOLD, color: '#000', border: 'none', padding: '12px 20px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem', width: '100%' };

const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' };
const modalBody = { background: '#010810', width: '100%', maxWidth: '600px', borderRadius: '30px', border: `1px solid ${GOLD}`, overflow: 'hidden' };
const modalHeader = { padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const closeBtn = { background: 'none', border: 'none', color: '#fff', fontSize: '1.2rem', cursor: 'pointer' };
const canvasContainer = { height: '400px', background: '#000' };
const healthSection = { padding: '25px', textAlign: 'center' };
const healthLabel = { fontSize: '0.65rem', color: GOLD, fontWeight: '900', letterSpacing: '2px', marginBottom: '10px', textTransform: 'uppercase' };
const alertBox = { border: '1px solid #ff4444', color: '#ff4444', padding: '12px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold', background: 'rgba(255, 68, 68, 0.05)' };
const returnBtn = { background: 'rgba(255,255,255,0.05)', color: '#fff', border: 'none', width: '100%', padding: '15px', marginTop: '20px', borderRadius: '15px', cursor: 'pointer', fontWeight: 'bold' };
